import React, { FC, useRef } from 'react';
import { NewOrderType } from 'types/order';
import { toISO8601LocalString } from 'utils/helpers';

import { tableRowComponent } from '../../services';
import useScheduleMeeting from './ron-schedule-meeting-hooks';
import RonScheduleMeetingScene from './ron-schedule-meeting-scene';
import { PartyItem, scheduleMettingTokens } from './services';

type RonScheduleMeetingtype = {
  open: boolean;
  onClose: () => void;
  onCancel: () => void;
  tokensInfo: scheduleMettingTokens;
  setTokensRon: React.Dispatch<React.SetStateAction<tableRowComponent[]>>;
  graphOrder: NewOrderType | undefined,
  tokenParties: string[]
}

// The parent rebuilds `tokensInfo` / `tokenParties` on every render. Keep the previous
// object identities until their CONTENT changes so the form-reset effect in the hook
// does not wipe the user's edits on unrelated re-renders (e.g. a viewport resize).
const useStableByContent = <T, >(value: T, contentKey: string): T => {
  const ref = useRef({ contentKey, value });
  if (ref.current.contentKey !== contentKey) {
    ref.current = { contentKey, value };
  }
  return ref.current.value;
};

const RonScheduleMeeting: FC<RonScheduleMeetingtype> = ({
  open,
  onCancel,
  onClose,
  tokensInfo: tokensInfoProp,
  setTokensRon,
  graphOrder,
  tokenParties: tokenPartiesProp
}) => {
  const tokensInfo = useStableByContent(
    tokensInfoProp,
    JSON.stringify([
      tokensInfoProp.date,
      tokensInfoProp.init_time,
      tokensInfoProp.end_time,
      tokensInfoProp.id,
      tokensInfoProp.parties.map((party) => party?._id)
    ])
  );
  const tokenParties = useStableByContent(tokenPartiesProp, JSON.stringify(tokenPartiesProp));

  const {
    t,
    methods,
    currentAttendees,
    orderId,
    updpateTokens,
    loading,
    orderParties
  } = useScheduleMeeting(
    tokensInfo,
    onClose,
    setTokensRon,
    graphOrder,
    tokenParties
  );

  const handleUpdateTokens = (data: scheduleMettingTokens, parties: PartyItem[], reissue: boolean) => {
    if (!data.init_time && !data.end_time) return;

    const date = data.date.split("T")[0];
    const startTime = toISO8601LocalString(new Date(data.init_time!), false)!.split("T")[1];
    const endTime = toISO8601LocalString(new Date(data.end_time!), false)!.split("T")[1];

    updpateTokens({
      id: orderId!,
      parties: parties.map((party) => ({
        email_address: party.email
      })),
      notBefore: `${date}T${startTime}`,
      expiresIn: `${date}T${endTime}`,
      ...(reissue ? { reissue: true } : {})
    });
  };

  return (
    <RonScheduleMeetingScene
      t={t}
      methods={methods}
      open={open}
      onClose={onClose}
      onCancel={onCancel}
      currentAttendees={currentAttendees}
      handleUpdateTokens={handleUpdateTokens}
      loading={loading}
      orderParties={orderParties}
      canReissue={tokenParties.length > 0}
    />
  );
};

export default RonScheduleMeeting;
